import React, { useState } from 'react';
import { Button, Modal, Steps, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { usePWAInstall } from '../../hooks/usePWAInstall';

const { Text, Paragraph } = Typography;

// iOS share icon (same one Apple uses in Safari)
const IOSShareIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20" height="20" viewBox="0 0 50 50"
    fill="currentColor"
    style={{ verticalAlign: 'middle' }}
  >
    <path d="M30.3 8.5l-4.8-4.8v28.1h-1V3.7L19.7 8.5l-.7-.7 6-6 6 6-.7.7z" />
    <path d="M40 16H10a2 2 0 0 0-2 2v22a2 2 0 0 0 2 2h30a2 2 0 0 0 2-2V18a2 2 0 0 0-2-2zm1 24a1 1 0 0 1-1 1H10a1 1 0 0 1-1-1V18a1 1 0 0 1 1-1h30a1 1 0 0 1 1 1v22z" />
    <path d="M17 23h2v1h-2zm7 0h2v1h-2zm7 0h2v1h-2z" />
  </svg>
);

interface Props {
  /** Renders a compact icon-only button — suitable for tight headers */
  compact?: boolean;
  /** Override the button colour when header is transparent */
  textColor?: string;
}

const PWAInstallButton: React.FC<Props> = ({ compact = false, textColor }) => {
  const { canInstall, isIOS, promptInstall, dismiss } = usePWAInstall();
  const [iosModalOpen, setIosModalOpen] = useState(false);

  if (!canInstall) return null;

  const handleClick = async () => {
    if (isIOS) {
      setIosModalOpen(true);
    } else {
      await promptInstall();
    }
  };

  const handleIOSDismiss = () => {
    setIosModalOpen(false);
    dismiss();
  };

  return (
    <>
      <Button
        size="small"
        icon={<DownloadOutlined />}
        onClick={handleClick}
        style={{
          borderRadius: 8,
          fontWeight: 600,
          fontSize: 12,
          color: textColor ?? '#56B0FF',
          borderColor: textColor ? `${textColor}55` : '#56B0FF55',
          background: 'transparent',
          whiteSpace: 'nowrap',
        }}
      >
        {!compact && 'Install App'}
      </Button>

      {/* iOS guide modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <DownloadOutlined style={{ color: '#56B0FF', fontSize: 18 }} />
            <span>Add WorkWav to your Home Screen</span>
          </div>
        }
        open={iosModalOpen}
        onCancel={handleIOSDismiss}
        footer={
          <Button type="primary" onClick={handleIOSDismiss} style={{ width: '100%' }}>
            Got it
          </Button>
        }
        width={360}
        centered
      >
        <Paragraph style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>
          Safari on iPhone doesn't have an automatic install button, but you can
          add WorkWav to your home screen in two taps:
        </Paragraph>

        <Steps
          direction="vertical"
          size="small"
          style={{ marginLeft: 4 }}
          items={[
            {
              title: (
                <Text strong style={{ fontSize: 14 }}>
                  Tap the Share button
                </Text>
              ),
              description: (
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
                  Look for the{' '}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#007AFF' }}>
                    <IOSShareIcon />
                  </span>{' '}
                  share icon at the bottom of your Safari browser bar.
                </div>
              ),
              status: 'process',
            },
            {
              title: (
                <Text strong style={{ fontSize: 14 }}>
                  Tap "Add to Home Screen"
                </Text>
              ),
              description: (
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
                  Scroll down in the share sheet and tap{' '}
                  <strong style={{ color: '#007AFF' }}>Add to Home Screen</strong>,
                  then tap <strong>Add</strong> in the top-right corner.
                </div>
              ),
              status: 'wait',
            },
          ]}
        />

        <div style={{
          marginTop: 20, padding: '12px 16px',
          background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd',
        }}>
          <Text style={{ fontSize: 13, color: '#0369a1' }}>
            WorkWav will appear on your home screen like a native app — no App Store needed!
          </Text>
        </div>
      </Modal>
    </>
  );
};

export default PWAInstallButton;
